/**
 * Detects if the current browser is iOS Safari
 * @returns {boolean} True if running on iOS Safari
 */
export const isIOSSafari = () => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isSafari = /Safari/.test(userAgent) && !/Chrome|CriOS|FxiOS/.test(userAgent);
  
  // Also check for iOS 13+ which reports as MacOS
  const isIOS13Plus = /Macintosh/.test(userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1;
  
  return (isIOS && isSafari) || isIOS13Plus;
};

/**
 * Sets up push notification service worker and requests permission
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export const setupPushNotifications = async () => {
  const isIOS = isIOSSafari();
  
  // iOS Safari: Service workers work but notifications have limitations
  // Still register service worker for future use, but notifications will use regular API
  if (!('serviceWorker' in navigator)) {
    return null;
  }

  // Don't require PushManager for iOS Safari (it may not be available)
  if (!isIOS && !('PushManager' in window)) {
    // Still continue for iOS Safari
  }

  try {
    // Use Firebase messaging SW so FCM background + local notifications and GET_PENDING_FCM_CLICK work
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

    await navigator.serviceWorker.ready;

    // Request notification permission
    // Note: iOS Safari REQUIRES user interaction (click) to show permission prompt
    // So we DON'T request it automatically on iOS - user must click the banner button
    if (Notification.permission === 'default') {
      if (isIOS) {
        // Don't try to request permission automatically on iOS - it will fail
        // User must click the banner button
      } else {
        try {
          await Notification.requestPermission();
        } catch (error) {
          // Could not request permission (may require user interaction)
        }
      }
    }

    // Listen for messages from service worker
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'VIDEO_CALL_NOTIFICATION_CLICK') {
        // Dispatch custom event for handling notification click
        window.dispatchEvent(new CustomEvent('videoCallNotificationClick', {
          detail: {
            action: event.data.action,
            data: event.data.data
          }
        }));
      }
    });

    if (Notification.permission === 'granted' && 'PushManager' in window) {
      try {
        await subscribeToPush(registration);
      } catch (error) {
        // Could not subscribe to push
      }
    }

    return registration;
  } catch (error) {
    return null;
  }
};

/**
 * Debug function to check notification and service worker status
 * Call this from browser console: window.checkNotificationStatus()
 */
export const checkNotificationStatus = async () => {
  if ('serviceWorker' in navigator) {
    try {
      await navigator.serviceWorker.getRegistration();
    } catch (error) {
      // Ignore
    }
  }

  return {
    notificationSupported: 'Notification' in window,
    permission: Notification.permission,
    serviceWorkerSupported: 'serviceWorker' in navigator,
    pageVisible: !document.hidden
  };
};

/**
 * Request notification permission (if denied, user must enable in browser settings)
 * Call this from browser console: window.requestNotificationPermission()
 */
export const requestNotificationPermission = async () => {
  const isIOS = isIOSSafari();

  if (!('Notification' in window)) {
    if (isIOS) {
      alert('Notifications are not supported in this browser version. Please update Safari.');
    }
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission === 'denied') {
    if (isIOS) {
      alert('Notification permission is denied. To enable:\n\n1. Open Settings app\n2. Go to Safari → Notifications\n3. Find this website and enable\n4. Refresh this page');
    } else {
      alert('Notification permission is denied. Please enable it in your browser settings:\n\nChrome: Settings → Privacy → Site Settings → Notifications\n\nThen refresh this page.');
    }
    return false;
  }

  try {
    const permission = await Notification.requestPermission();

    if (permission === 'granted') {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        try {
          const registration = await navigator.serviceWorker.ready;
          await subscribeToPush(registration);
        } catch (error) {
          // Continue anyway - local notifications will still work
        }
      }
      return true;
    } else if (permission === 'denied') {
      if (isIOS) {
        alert('Notification permission was denied. To enable later:\n\nSettings → Safari → Notifications');
      }
      return false;
    } else {
      return false;
    }
  } catch (error) {
    if (isIOS) {
      alert('Failed to request notification permission. Please try clicking the button again.');
    }
    return false;
  }
};

/** Full URL for notification icon (Chrome requires absolute URL to show custom icon) */
const getNotificationIconUrl = () =>
  (typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '') + '/oatrx-favicon.png';

/**
 * Test notification function
 * Call this from browser console: window.testNotification()
 */
export const testNotification = async () => {
  if (!('Notification' in window)) {
    return;
  }

  if (Notification.permission !== 'granted') {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return;
    }
  }

  const iconUrl = getNotificationIconUrl();

  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      if (registration.active) {
        await registration.showNotification('Test Notification', {
          body: 'This is a test notification from service worker',
          icon: iconUrl,
          tag: 'test-notification',
          requireInteraction: true
        });
        return;
      }
    } catch (error) {
      // Fall through to regular notification
    }
  }

  if (document.visibilityState === 'visible') {
    const notification = new Notification('Test Notification', {
      body: 'This is a test notification',
      icon: iconUrl,
      tag: 'test-notification'
    });
    notification.onclick = () => {
      notification.close();
    };
  }
};

// Make functions available globally for debugging
if (typeof window !== 'undefined') {
  window.checkNotificationStatus = checkNotificationStatus;
  window.testNotification = testNotification;
  window.requestNotificationPermission = requestNotificationPermission;
}

/**
 * Converts VAPID public key from base64url to Uint8Array
 * @param {string} base64String - Base64 URL-safe encoded VAPID public key
 * @returns {Uint8Array} - Decoded key as Uint8Array
 */
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

/**
 * Gets VAPID public key for PushManager subscription (optional path, e.g. Safari).
 * FCM token is obtained via firebase.js (getToken with VAPID from env or firebase.js).
 * @returns {Promise<string|null>} - VAPID public key or null
 */
const getVapidPublicKey = async () => {
  if (process.env.REACT_APP_VAPID_PUBLIC_KEY) {
    return process.env.REACT_APP_VAPID_PUBLIC_KEY;
  }
  // Optional: fetch from backend (e.g. get_vapid_public_key) if you expose it
  return null;
};

/**
 * Subscribes to push notifications with VAPID (required for Safari web push)
 * @param {ServiceWorkerRegistration} registration - Service worker registration
 * @returns {Promise<PushSubscription|null>}
 */
export const subscribeToPush = async (registration) => {
  if (!registration) {
    return null;
  }

  if (!('PushManager' in window)) {
    return null;
  }

  let subscription = await registration.pushManager.getSubscription();
  if (subscription) {
    return subscription;
  }

  try {
    const vapidPublicKey = await getVapidPublicKey();

    if (!vapidPublicKey) {
      return null;
    }

    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey
    });

    return subscription;
  } catch (error) {
    return null;
  }
};

/**
 * Sends push notification via service worker
 * @param {Object} params - Notification parameters
 * @param {string} params.title - Notification title
 * @param {string} params.body - Notification body
 * @param {string} params.icon - Notification icon URL
 * @param {Object} params.data - Additional data
 * @returns {Promise<void>}
 */
export const sendPushViaServiceWorker = async ({
  title,
  body,
  icon,
  data
}) => {
  const iconUrl = getNotificationIconUrl();
  try {
    const registration = await navigator.serviceWorker.ready;

    // Use unique tag for each notification to prevent replacement
    const uniqueTag = data?.callId
      ? `video-call-${data.callId}`
      : `video-call-${Date.now()}-${Math.random()}`;

    await registration.showNotification(title, {
      body,
      icon: icon || iconUrl,
      badge: iconUrl,
      tag: uniqueTag, // Unique tag prevents notification replacement
      data: data || {},
      requireInteraction: true,
      silent: false, // Enable sound
      actions: [
        {
          action: 'accept',
          title: 'Accept'
        },
        {
          action: 'decline',
          title: 'Decline'
        }
      ]
    });
  } catch (error) {
    // Fallback to regular notification
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        body,
        icon: icon || iconUrl,
        tag: 'video-call-request',
        data: data || {}
      });
    }
  }
};

